import { useState } from "react";
import { Search, ChevronRight, Plus, Sparkles } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { db } from "@/lib/supabaseUntyped";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { 
  DEMARCHES_CATALOG, 
  DEMARCHE_CATEGORIES, 
  DemarcheTemplate,
  DemarcheCategory,
  searchDemarches,
  getDemarchesByCategory 
} from "@/config/demarchesConfig";

interface CreateDemarcheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

type Mode = 'choose' | 'template' | 'free';

export function CreateDemarcheDialog({ open, onOpenChange, onCreated }: CreateDemarcheDialogProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DemarcheCategory | 'all'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<DemarcheTemplate | null>(null);
  const [freeTitle, setFreeTitle] = useState('');
  const [freeDescription, setFreeDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setMode('choose');
    setSearch('');
    setSelectedCategory('all');
    setSelectedTemplate(null);
    setFreeTitle('');
    setFreeDescription('');
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(resetState, 200);
  };

  // Filter templates
  const filteredTemplates = search 
    ? searchDemarches(search)
    : selectedCategory === 'all' 
      ? DEMARCHES_CATALOG 
      : getDemarchesByCategory(selectedCategory);

  const handleSelectTemplate = (template: DemarcheTemplate) => {
    setSelectedTemplate(template);
    setMode('template');
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const { data: user } = await db.auth.getUser();
      if (!user.user) throw new Error("Non authentifié");

      // Create demarche
      const { data: demarche, error: demarcheError } = await db
        .from('demarches')
        .insert({
          user_id: user.user.id,
          template_key: selectedTemplate.key,
          title: selectedTemplate.label,
          description: selectedTemplate.description,
          official_url: selectedTemplate.official_url,
          status: 'pending',
        })
        .select()
        .single();

      if (demarcheError) throw demarcheError;

      // Create documents
      if (selectedTemplate.documents.length > 0) {
        const docsToInsert = selectedTemplate.documents.map((doc, index) => ({
          demarche_id: demarche.id,
          label: doc.label,
          doc_type: doc.doc_type,
          is_required: doc.is_required,
          validity_rule: doc.validity_rule || null,
          sort_order: index,
        }));

        const { error: docsError } = await db
          .from('demarche_documents')
          .insert(docsToInsert);

        if (docsError) throw docsError;
      }

      // Create steps
      if (selectedTemplate.steps.length > 0) {
        const stepsToInsert = selectedTemplate.steps.map((step, index) => ({
          demarche_id: demarche.id,
          label: step.label,
          step_type: step.step_type,
          url: step.url || null,
          sort_order: index,
        }));

        const { error: stepsError } = await db
          .from('demarche_steps')
          .insert(stepsToInsert);

        if (stepsError) throw stepsError;
      }

      toast.success('Démarche créée !');
      handleClose();
      onCreated();
    } catch (error) {
      console.error('Error creating demarche:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFree = async () => {
    if (!freeTitle.trim()) {
      toast.error('Veuillez saisir un titre');
      return;
    }

    setLoading(true);
    try {
      const { data: user } = await db.auth.getUser();
      if (!user.user) throw new Error("Non authentifié");

      const { error } = await db
        .from('demarches')
        .insert({
          user_id: user.user.id,
          title: freeTitle.trim(),
          description: freeDescription.trim() || null,
          status: 'pending',
        });

      if (error) throw error;

      toast.success('Démarche créée !');
      handleClose();
      onCreated();
    } catch (error) {
      console.error('Error creating demarche:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // Get icon component
  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="w-5 h-5" /> : <LucideIcons.FileText className="w-5 h-5" />;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>
            {mode === 'choose' && 'Nouvelle démarche'}
            {mode === 'template' && selectedTemplate?.label}
            {mode === 'free' && 'Démarche libre'}
          </DialogTitle>
        </DialogHeader>

        {/* Choose mode */}
        {mode === 'choose' && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="px-6 py-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une démarche..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Category tabs */}
            {!search && (
              <div className="px-6">
                <ScrollArea className="w-full">
                  <div className="flex gap-2 pb-2">
                    <Button
                      variant={selectedCategory === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory('all')}
                      className="shrink-0"
                    >
                      Toutes
                    </Button>
                    {Object.entries(DEMARCHE_CATEGORIES).map(([key, cat]) => (
                      <Button
                        key={key}
                        variant={selectedCategory === key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory(key as DemarcheCategory)}
                        className="shrink-0"
                      >
                        {cat.label}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Template list */}
            <ScrollArea className="flex-1 px-6 py-4">
              <div className="space-y-2">
                {/* Free demarche option */}
                <button
                  onClick={() => setMode('free')}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
                >
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Plus className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Démarche libre</p>
                    <p className="text-xs text-muted-foreground">
                      Créer une démarche personnalisée
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>

                {/* Separator */}
                <div className="flex items-center gap-3 py-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">ou choisir un modèle</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Templates */}
                {filteredTemplates.map((template) => {
                  const category = DEMARCHE_CATEGORIES[template.category];
                  return (
                    <button
                      key={template.key}
                      onClick={() => handleSelectTemplate(template)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className={cn("p-2 rounded-lg", category.color, "text-white")}>
                        {getIcon(template.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{template.label}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {template.documents.length} docs · {template.steps.length} étapes
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </button>
                  );
                })}

                {filteredTemplates.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Aucune démarche trouvée
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Template preview */}
        {mode === 'template' && selectedTemplate && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <ScrollArea className="flex-1 px-6 py-4">
              <p className="text-sm text-muted-foreground mb-4">
                {selectedTemplate.description}
              </p>

              {selectedTemplate.official_url && (
                <a
                  href={selectedTemplate.official_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline mb-4"
                >
                  Voir sur service-public.fr
                  <ChevronRight className="w-3 h-3" />
                </a>
              )}

              {/* Documents preview */}
              <div className="mb-4">
                <h4 className="text-sm font-medium mb-2">
                  Documents requis ({selectedTemplate.documents.length})
                </h4>
                <div className="space-y-1.5">
                  {selectedTemplate.documents.map((doc, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      <span className="flex-1">{doc.label}</span>
                      {!doc.is_required && (
                        <Badge variant="outline" className="text-[10px]">Optionnel</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Steps preview */}
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Étapes ({selectedTemplate.steps.length})
                </h4>
                <div className="space-y-1.5">
                  {selectedTemplate.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                      <span className="flex-1">{step.label}</span>
                      {step.url && (
                        <Badge variant="secondary" className="text-[10px]">Lien</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>

            <div className="p-6 pt-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setMode('choose')} className="flex-1">
                Retour
              </Button>
              <Button onClick={handleCreateFromTemplate} disabled={loading} className="flex-1">
                {loading ? 'Création...' : 'Créer cette démarche'}
              </Button>
            </div>
          </div>
        )}

        {/* Free demarche form */}
        {mode === 'free' && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="px-6 py-4 space-y-4">
              <div>
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  placeholder="Ex: Demande de crédit"
                  value={freeTitle}
                  onChange={(e) => setFreeTitle(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="description">Description (optionnel)</Label>
                <Textarea
                  id="description"
                  placeholder="Décrivez cette démarche..."
                  value={freeDescription}
                  onChange={(e) => setFreeDescription(e.target.value)}
                  className="mt-1.5"
                  rows={3}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Vous pourrez ajouter des documents et des étapes après la création.
              </p>
            </div>

            <div className="p-6 pt-4 border-t flex gap-2 mt-auto">
              <Button variant="outline" onClick={() => setMode('choose')} className="flex-1">
                Retour
              </Button>
              <Button onClick={handleCreateFree} disabled={loading || !freeTitle.trim()} className="flex-1">
                {loading ? 'Création...' : 'Créer'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
